import { useEffect, useRef } from 'react';

import {
  MY_CHECKS_PARAMS,
  MY_SUB_CHECKS_PARAMS,
  buildIssuesViewParams,
  buildMissingResultViewParams,
  buildPrioritizeViewParams,
  buildUnassignedViewParams,
} from 'helpers/admin-candidate-checks';

import { Sidebar } from 'components/Sidebar/Sidebar';

import { useGetAdminSidebarCountsQuery } from 'api/candidate-checks-api';

export const ChecksSidebarWrapper = ({
  savedViewsState,
  activeSmartFilterId,
  setActiveSmartFilterId,
  overdueSupportHours,
  skipInitialDefault = false,
  currentSupportUser = null,
}) => {
  const { data: counts } = useGetAdminSidebarCountsQuery({
    overdue_support_hours: overdueSupportHours,
  });

  const applySmartFilter = (id, params) => {
    setActiveSmartFilterId(id);
    savedViewsState.setActiveViewId(null);
    savedViewsState.setPendingApply({ params, key: Date.now() });
  };

  const applySavedView = view => {
    setActiveSmartFilterId(null);
    savedViewsState.setActiveViewId(view.id);
    savedViewsState.setPendingApply({ params: view.savedQueryParams ?? {}, key: Date.now() });
  };

  // Initial-load default: a saved default view wins if one exists (handled elsewhere by
  // useSavedViews' own default-view-apply mechanism). Otherwise default to "My checks" if
  // the admin has any, falling back to "All checks" if they don't.
  // When returning via browser back, skip this so the restored session filters win.
  const initialViewAppliedRef = useRef(false);

  useEffect(() => {
    if (initialViewAppliedRef.current) return;
    if (skipInitialDefault) {
      initialViewAppliedRef.current = true;
      return;
    }
    if (!savedViewsState.savedViewsReady) return;
    if (savedViewsState.defaultView) return;
    if (!counts) return;

    initialViewAppliedRef.current = true;

    if (counts.my_checks > 0) {
      applySmartFilter('my_checks', MY_CHECKS_PARAMS);
    } else {
      setActiveSmartFilterId('all_checks');
    }
  }, [savedViewsState.savedViewsReady, savedViewsState.defaultView, counts, skipInitialDefault]);

  const supportUser = (counts?.my_checks ?? 0) > 0 ? currentSupportUser : null;
  const prioritizeItem = (id, label, value, name) => ({
    id,
    label,
    active: activeSmartFilterId === id,
    onClick: () => applySmartFilter(id, buildPrioritizeViewParams(value, name, { supportUser })),
  });

  const sections = [
    {
      title: 'My Work',
      items: [
        {
          id: 'my_checks',
          label: 'My checks',
          count: counts?.my_checks,
          active: activeSmartFilterId === 'my_checks',
          onClick: () => applySmartFilter('my_checks', MY_CHECKS_PARAMS),
        },
        {
          id: 'my_sub_checks',
          label: 'My sub checks',
          count: counts?.my_sub_checks,
          active: activeSmartFilterId === 'my_sub_checks',
          onClick: () => applySmartFilter('my_sub_checks', MY_SUB_CHECKS_PARAMS),
        },
      ],
    },
    {
      title: 'Prioritize',
      items: [
        prioritizeItem('intercom', 'Intercom reply', 'intercom', 'Intercom reply'),
        prioritizeItem('next_action_due', 'Next action due', 'due', 'Next action due'),
        prioritizeItem(
          'overdue_provider',
          'Overdue provider',
          'overdue_provider',
          'Overdue provider',
        ),
        prioritizeItem(
          'overdue_support',
          'Overdue support',
          'overdue_support',
          `Overdue support (${overdueSupportHours}h)`,
        ),
      ],
    },
    {
      title: 'Issues',
      items: [
        {
          id: 'id_requirements',
          label: 'ID requirements not met',
          active: activeSmartFilterId === 'id_requirements',
          onClick: () =>
            applySmartFilter(
              'id_requirements',
              buildIssuesViewParams(['id_not_met'], ['ID requirements not met']),
            ),
        },
        {
          id: 'bot_failed',
          label: 'Bot failed',
          active: activeSmartFilterId === 'bot_failed',
          onClick: () =>
            applySmartFilter('bot_failed', buildIssuesViewParams(['failed'], ['Bot failed'])),
        },
        {
          id: 'email_failed',
          label: 'Email failed',
          active: activeSmartFilterId === 'email_failed',
          onClick: () =>
            applySmartFilter(
              'email_failed',
              buildIssuesViewParams(
                ['candidate_email_failed', 'provider_email_failed'],
                ['Candidate email failed', 'Provider email failed'],
              ),
            ),
        },
      ],
    },
    {
      title: 'Audit',
      items: [
        {
          id: 'missing_result',
          label: 'Missing result file',
          active: activeSmartFilterId === 'missing_result',
          onClick: () => applySmartFilter('missing_result', buildMissingResultViewParams()),
        },
        {
          id: 'unassigned',
          label: 'Unassigned',
          active: activeSmartFilterId === 'unassigned',
          onClick: () => applySmartFilter('unassigned', buildUnassignedViewParams()),
        },
      ],
    },
  ];

  if (savedViewsState.savedViews.length > 0) {
    sections.push({
      title: 'Saved Views',
      items: savedViewsState.savedViews.map(view => ({
        id: `saved_view_${view.id}`,
        label: view.name,
        active: Number(savedViewsState.activeViewId) === Number(view.id),
        onClick: () => applySavedView(view),
      })),
    });
  }

  return <Sidebar sections={sections} />;
};
